// WriteOffModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import AutocompleteSelect from '../../../../cabinet/wizplain/components/Autocomplete/AutocompleteSelect';

const reasonOptions = ['Псування', 'Недостача', 'Пересортиця', 'Інше'];

const emptyForm = { baseProductId: '', productId: '', quantity: '', reasonType: 'Псування', reasonNote: '' };

const WriteOffModal = ({ isOpen, onClose, onSubmit, balance = [] }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm);
      setError(null);
    }
  }, [isOpen]);

  const selectedBase = balance.find(b => b._id === form.baseProductId);
  const variants = (selectedBase && selectedBase.variants) || [];
  const hasMultipleVariants = variants.length > 1;
  const selectedVariant = variants.find(v => v.productId === form.productId);

  // What the write-off will actually be limited to and measured in: the specific variant if one
  // is picked, otherwise the base product's combined total (any brand, FIFO).
  const availableQuantity = selectedVariant ? selectedVariant.quantityRemaining : (selectedBase ? selectedBase.quantityRemaining : undefined);
  const unit = selectedVariant ? selectedVariant.unit : (selectedBase ? selectedBase.unit : '');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.baseProductId || !form.quantity) {
      setError('Оберіть продукт і вкажіть кількість');
      return;
    }

    if (availableQuantity !== undefined && Number(form.quantity) > availableQuantity) {
      setError(`Недостатньо залишку: доступно ${availableQuantity} ${unit}`);
      return;
    }

    const reason = form.reasonType === 'Інше' && form.reasonNote
      ? form.reasonNote
      : (form.reasonType === 'Інше' ? '' : form.reasonType);

    if (!reason) {
      setError('Вкажіть причину списання');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        // Specific brand if chosen; otherwise write off "any brand" of the base product (FIFO
        // across all its trade names/variants).
        productId: form.productId || undefined,
        baseProductId: form.productId ? undefined : form.baseProductId,
        quantity: Number(form.quantity),
        reason
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="md">
      <Modal.Header closeButton>
        <Modal.Title>Списати продукцію</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          <Form.Group className="mb-3">
            <AutocompleteSelect
              label="Продукт *"
              value={form.baseProductId}
              onChange={(id) => setForm(prev => ({ ...prev, baseProductId: id, productId: '' }))}
              options={balance.map(b => ({ ...b, description: `залишок ${b.quantityRemaining} ${b.unit}` }))}
              fieldsValue={['baseProductName']}
              placeholder="Почніть вводити назву продукту зі складу..."
            />
            {balance.length === 0 && (
              <Form.Text className="text-muted">На складі немає продукції для списання.</Form.Text>
            )}
          </Form.Group>

          {hasMultipleVariants && (
            <Form.Group className="mb-3">
              <Form.Label>Торгова назва (необовʼязково)</Form.Label>
              <Form.Select name="productId" value={form.productId} onChange={handleChange}>
                <option value="">Без різниці — списати з будь-якого бренду</option>
                {variants.map(v => (
                  <option key={v.productId} value={v.productId}>
                    {v.productName} — залишок {v.quantityRemaining} {v.unit}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Оберіть конкретний бренд, якщо псування стосується саме його — інакше система
                спише з найстаріших партій незалежно від бренду.
              </Form.Text>
            </Form.Group>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Кількість *{unit ? ` (${unit})` : ''}</Form.Label>
                <Form.Control
                  type="number"
                  step="0.001"
                  min="0"
                  max={availableQuantity}
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Причина *</Form.Label>
                <Form.Select name="reasonType" value={form.reasonType} onChange={handleChange}>
                  {reasonOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {form.reasonType === 'Інше' && (
            <Form.Group className="mb-3">
              <Form.Label>Опишіть причину</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="reasonNote"
                value={form.reasonNote}
                onChange={handleChange}
                required
              />
            </Form.Group>
          )}

          <Form.Text className="text-muted d-block">
            Списання відбувається автоматично з найстаріших партій (спершу ті, у яких раніше
            спливає термін придатності).
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Скасувати</Button>
          <Button variant="danger" type="submit" disabled={submitting || balance.length === 0}>
            {submitting && <Spinner animation="border" size="sm" className="me-2" />}
            Списати
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default WriteOffModal;